// src/SubscriptionsPage.js - Subscriptions management page
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Chip,
  Button,
  TextField,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { supabase } from './supabaseClient';

import { brand, ink, status, surface } from './designTokens';
import { prettyStatus } from './labels';
export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  // Plan editing. `editingPlan` null = closed; {} = creating a new plan.
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planSnack, setPlanSnack] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    mrr: 0,
    activeSubscriptions: 0,
    arpu: 0,
    churnRate: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch user subscriptions (actual user records)
      const { data: userSubs, error: userSubsError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .order('start_date', { ascending: false });

      if (userSubsError) throw userSubsError;

      // Fetch plans catalog
      const { data: plansData, error: plansError } = await supabase
        .from('Subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (plansError) {
        console.warn('Error fetching plans:', plansError);
        setPlans([]);
      } else {
        setPlans(plansData || []);
      }

      // Fetch profiles by phone number
      const phoneNumbers = [...new Set((userSubs || []).map(s => s.phone_number).filter(Boolean))];
      const profileMapByPhone = new Map();
      if (phoneNumbers.length > 0) {
        const { data: profiles } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, email, phone_number, profile_image')
          .in('phone_number', phoneNumbers);

        if (profiles) {
          profiles.forEach(p => {
            if (p.profile_image) {
              try {
                const { data: urlData } = supabase
                  .storage
                  .from('profile-images')
                  .getPublicUrl(`profiles/${p.profile_image}`);
                p.profile_image_url = urlData.publicUrl;
              } catch (err) {
                console.error('Error getting profile image URL:', err);
              }
            }
            if (p.phone_number) profileMapByPhone.set(p.phone_number, p);
          });
        }
      }

      // Plan map
      const planMap = new Map();
      (plansData || []).forEach(p => {
        if (p.id) planMap.set(p.id, p);
      });

      // Count subscribers per plan
      const planSubscriberCount = new Map();
      (userSubs || []).forEach(sub => {
        if (sub.subscription_id && (sub.status || '').toLowerCase() === 'active') {
          const count = planSubscriberCount.get(sub.subscription_id) || 0;
          planSubscriberCount.set(sub.subscription_id, count + 1);
        }
      });

      // Enrich plans with subscriber counts
      const enrichedPlans = (plansData || []).map(plan => ({
        ...plan,
        subscribers: planSubscriberCount.get(plan.id) || 0,
      }));

      setPlans(enrichedPlans);

      // Enrich subscriptions
      const enriched = (userSubs || []).map(sub => {
        const plan = sub.subscription_id ? planMap.get(sub.subscription_id) : null;
        const profile = sub.phone_number ? profileMapByPhone.get(sub.phone_number) : null;
        const price = sub.renewal_period === 'yearly'
          ? (plan?.yearly_price ? parseFloat(plan.yearly_price) : null)
          : (plan?.monthly_price ? parseFloat(plan.monthly_price) : null);

        return {
          ...sub,
          profile: profile || {},
          plan,
          plan_name: plan?.display_name || sub.plan_name || 'Subscription',
          amount: price ?? sub.amount ?? 0,
        };
      });

      setSubscriptions(enriched);
      setAllSubscriptions(enriched);

      // Stats
      const activeSubs = enriched.filter(s => (s.status || '').toLowerCase() === 'active');
      const mrr = activeSubs.reduce((sum, s) => {
        let price = 0;
        if (s.renewal_period === 'yearly') {
          price = s.plan?.yearly_price ? parseFloat(s.plan.yearly_price) / 12 : (s.amount || 0);
        } else {
          price = s.plan?.monthly_price ? parseFloat(s.plan.monthly_price) : (s.amount || 0);
        }
        return sum + price;
      }, 0);
      const arpu = activeSubs.length > 0 ? mrr / activeSubs.length : 0;

      setStats({
        mrr,
        activeSubscriptions: activeSubs.length,
        arpu,
        churnRate: 0, // not enough data for real churn here
      });
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter subscriptions
  useEffect(() => {
    if (!searchTerm) {
      setSubscriptions(allSubscriptions);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allSubscriptions.filter(sub => {
      const profile = sub.profile || {};
      const matchesName = `${profile.first_name || ''} ${profile.last_name || ''}`.toLowerCase().includes(term);
      const matchesEmail = profile.email?.toLowerCase().includes(term);
      const matchesPlan = sub.plan_name?.toLowerCase().includes(term);
      return matchesName || matchesEmail || matchesPlan;
    });

    setSubscriptions(filtered);
  }, [searchTerm, allSubscriptions]);

  const getProfileImageUrl = (profile) => {
    if (!profile) return null;
    if (profile.profile_image_url) return profile.profile_image_url;
    if (profile.profile_image) {
      const { data } = supabase.storage
        .from('profile-images')
        .getPublicUrl(`profiles/${profile.profile_image}`);
      return data.publicUrl;
    }
    return null;
  };

  // Create or update a platform plan.
  //
  // Prices are stored as written rather than converted to cents: the existing
  // rows are decimals and the cards already parseFloat them, so a unit change
  // here would silently divide every displayed price by a hundred.
  //
  // Features arrive as one per line and are stored as an array, which is the
  // shape the cards already map over.
  const savePlan = async (draft) => {
    setSavingPlan(true);
    const row = {
      display_name: (draft.display_name || '').trim(),
      monthly_price: draft.monthly_price === '' || draft.monthly_price == null
        ? null : Number(draft.monthly_price),
      yearly_price: draft.yearly_price === '' || draft.yearly_price == null
        ? null : Number(draft.yearly_price),
      description_client: (draft.description_client || '').trim() || null,
      features: (draft.featuresText || '')
        .split('\n').map((f) => f.trim()).filter(Boolean),
      status: draft.status || 'active',
    };

    if (!row.display_name) {
      setPlanSnack({ open: true, message: 'A plan needs a name', severity: 'error' });
      setSavingPlan(false);
      return;
    }

    const { error } = draft.id
      ? await supabase.from('Subscriptions').update(row).eq('id', draft.id)
      : await supabase.from('Subscriptions').insert(row);

    setSavingPlan(false);
    if (error) {
      setPlanSnack({ open: true, message: `Could not save: ${error.message}`, severity: 'error' });
      return;
    }
    setPlanSnack({
      open: true,
      message: draft.id ? 'Plan updated' : 'Plan created',
      severity: 'success',
    });
    setEditingPlan(null);
    fetchData();
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Monthly Recurring Revenue
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              ${stats.mrr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: status.success, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowUpwardIcon sx={{ fontSize: 12 }} />
              2.1% from last month
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Active Subscriptions
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.activeSubscriptions.toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: status.success, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowUpwardIcon sx={{ fontSize: 12 }} />
              32 new this month
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Avg. Revenue Per User
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              ${stats.arpu.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: ink[500] }}>
              Across all plans
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Churn Rate
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.churnRate.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: status.danger, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowDownwardIcon sx={{ fontSize: 12 }} />
              0.2% from last month
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Subscription Plans Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Subscription Plans
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: ink[900],
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: ink[900] }
            }}
            onClick={() => setEditingPlan({ status: 'active' })}
          >
            Create New Plan
          </Button>
        </Box>
        <Grid container spacing={2}>
          {plans.map((plan, idx) => {
            // Determine most popular plan by subscriber count
            const maxSubscribers = Math.max(...plans.map(p => p.subscribers || 0));
            const isPopular = (plan.subscribers || 0) === maxSubscribers && maxSubscribers > 0;
            const isArchived = plan.status === 'archived';
            const monthlyPrice = plan.monthly_price ? parseFloat(plan.monthly_price) : 0;
            const yearlyPrice = plan.yearly_price ? parseFloat(plan.yearly_price) : 0;
            
            return (
              <Grid item xs={12} md={4} key={plan.id || idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: isPopular ? `2px solid ${brand.gold}` : `1px solid ${surface.line}`,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    boxShadow: isPopular ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none',
                  }}
                >
                  {isPopular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: brand.gold,
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      Most Popular
                    </Box>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {plan.display_name || 'Plan'}
                      </Typography>
                      <Chip
                        label={isArchived ? 'Archived' : 'Active'}
                        size="small"
                        sx={{
                          bgcolor: isArchived ? surface.sunken : status.successBg,
                          color: isArchived ? ink[900] : status.success,
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      ${monthlyPrice.toFixed(2)}
                      <Typography component="span" variant="body1" sx={{ color: ink[500], fontWeight: 500 }}>
                        /month
                      </Typography>
                    </Typography>
                    {yearlyPrice > 0 && (
                      <Typography variant="body2" sx={{ color: ink[500], mb: 1 }}>
                        ${yearlyPrice.toFixed(2)}/year
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: ink[500], mb: 2 }}>
                      {plan.description_client || plan.description || 'Core features for personal styling.'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(plan.features || ['1 Personal Stylist', '5 Closet Items/Month', 'Basic Support']).map((feature, fIdx) => (
                        <Box key={fIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckIcon sx={{ fontSize: 16, color: status.success }} />
                          <Typography variant="body2" sx={{ color: ink[500], fontSize: '0.875rem' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {plan.subscribers || 0} Subscribers
                    </Typography>
                    <Button
                      size="small"
                      sx={{
                        color: brand.gold,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { color: brand.goldDeep }
                      }}
                      onClick={() => setEditingPlan({
                        ...plan,
                        featuresText: (plan.features || []).join('\n'),
                      })}
                    >
                      {isArchived ? 'View Plan' : 'Edit Plan'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* All Subscriptions Table */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 3,
          border: `1px solid ${surface.line}`,
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: `1px solid ${surface.line}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              All Subscriptions
            </Typography>
            <Typography variant="body2" sx={{ color: ink[500] }}>
              Track and manage all user subscriptions.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <SearchIcon 
                sx={{ 
                  position: 'absolute', 
                  left: 12, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: ink[300],
                  fontSize: 20
                }} 
              />
              <TextField
                placeholder="Search user..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: 192,
                  '& .MuiOutlinedInput-root': {
                    pl: 4.5,
                    '& fieldset': {
                      borderColor: surface.line,
                    },
                  },
                }}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              sx={{
                borderColor: surface.line,
                color: ink[700],
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: ink[300], bgcolor: surface.page }
              }}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              sx={{
                borderColor: surface.line,
                color: ink[700],
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: ink[300], bgcolor: surface.page }
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: surface.page }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  User
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Plan
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Start Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Next Billing
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: ink[500] }}>
                    No subscriptions found
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((sub) => {
                  const profile = sub.profile || {};
                  const profileImageUrl = getProfileImageUrl(profile);
                  
                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={profileImageUrl}
                            sx={{ width: 32, height: 32, bgcolor: surface.line }}
                          >
                            {profile.first_name?.[0] || ''}{profile.last_name?.[0] || ''}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {profile.first_name} {profile.last_name || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: ink[500], fontSize: '0.7rem' }}>
                              {profile.email || sub.profile_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {sub.plan_name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prettyStatus(sub.status) || 'Active'}
                          size="small"
                          sx={{
                            bgcolor: sub.status === 'active' ? status.successBg : surface.sunken,
                            color: sub.status === 'active' ? status.success : ink[900],
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {sub.start_date || sub.created_at
                          ? new Date(sub.start_date || sub.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {sub.end_date || sub.renewal_date || sub.next_billing_date
                          ? new Date(sub.end_date || sub.renewal_date || sub.next_billing_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: ink[700] }}>
                        ${parseFloat(sub.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Plan editor. The same dialog creates and edits — a new plan is simply
          one with no id yet, which keeps one form to keep correct. */}
      <Dialog
        open={!!editingPlan}
        onClose={() => !savingPlan && setEditingPlan(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingPlan?.id ? 'Edit plan' : 'New plan'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Plan name"
              value={editingPlan?.display_name || ''}
              onChange={(e) => setEditingPlan((p) => ({ ...p, display_name: e.target.value }))}
              fullWidth size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Monthly price" type="number" size="small" fullWidth
                value={editingPlan?.monthly_price ?? ''}
                onChange={(e) => setEditingPlan((p) => ({ ...p, monthly_price: e.target.value }))}
                InputProps={{ startAdornment: <Box sx={{ mr: 0.5, color: ink[500] }}>$</Box> }}
              />
              <TextField
                label="Yearly price" type="number" size="small" fullWidth
                value={editingPlan?.yearly_price ?? ''}
                onChange={(e) => setEditingPlan((p) => ({ ...p, yearly_price: e.target.value }))}
                InputProps={{ startAdornment: <Box sx={{ mr: 0.5, color: ink[500] }}>$</Box> }}
                helperText="Leave empty if this plan is monthly only"
              />
            </Stack>
            <TextField
              label="Description"
              value={editingPlan?.description_client || ''}
              onChange={(e) => setEditingPlan((p) => ({ ...p, description_client: e.target.value }))}
              fullWidth size="small" multiline minRows={2}
              helperText="The line shown under the plan name in the app"
            />
            <TextField
              label="Features"
              value={editingPlan?.featuresText || ''}
              onChange={(e) => setEditingPlan((p) => ({ ...p, featuresText: e.target.value }))}
              fullWidth size="small" multiline minRows={4}
              helperText="One per line — each becomes a ticked item on the plan card"
            />
            <TextField
              select label="Status" size="small" sx={{ width: 220 }}
              value={editingPlan?.status || 'active'}
              onChange={(e) => setEditingPlan((p) => ({ ...p, status: e.target.value }))}
            >
              <MenuItem value="active">Active — offered in the app</MenuItem>
              <MenuItem value="archived">Archived — hidden from new subscribers</MenuItem>
            </TextField>
            {editingPlan?.subscribers > 0 && (
              <Alert severity="warning">
                {editingPlan.subscribers} {editingPlan.subscribers === 1 ? 'person is' : 'people are'} on
                this plan. Changing the price does not change what they are already
                being charged — existing subscriptions keep their agreed price until
                they are changed in Stripe.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPlan(null)} disabled={savingPlan}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingPlan}
            sx={{ bgcolor: brand.gold, '&:hover': { bgcolor: brand.goldDeep } }}
            onClick={() => savePlan(editingPlan)}
          >
            {savingPlan ? 'Saving…' : editingPlan?.id ? 'Save changes' : 'Create plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={planSnack.open}
        autoHideDuration={6000}
        onClose={() => setPlanSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={planSnack.severity}
          onClose={() => setPlanSnack((s) => ({ ...s, open: false }))}
        >
          {planSnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

